import { auth, db } from '../../src/firebase.js';
import { onAuthStateChanged, updateProfile, EmailAuthProvider, reauthenticateWithCredential, updatePassword, deleteUser } from "firebase/auth";
import { doc, getDoc, setDoc, deleteDoc, serverTimestamp, runTransaction, collection, query, where, getDocs, orderBy, limit } from "firebase/firestore";
import { logOut, sendVerification } from '../../src/auth.js';
import { CROWDFUNDING_PROJECTS } from '../data/crowdfundingData.js';
import { getSavedProjects, unsaveProject } from '../data/savedProjects.js';

export class Profile {
    constructor(containerId) {
        this.containerId = containerId;
        this.currentUser = null;
        this.userData = {}; // Stores Firestore data
        this.savedItems = []; // Mock data for now
        this.savedProjects = [];
        this.orders = [];
        this.investments = [];
        this.weekWorkoutCount = 0;
        this.isOrdersLoading = true;
        
        // UI State
        this.activeTab = 'settings'; // settings, orders, saved
        this.viewingOrderId = null;
        this.activeSavedTab = 'podcasts';
        this.isLanguageOpen = false;
        
        this.languages = [
            "English", "Español", "Français", "Deutsch", "Português", "中文", "العربية", 
            "日本語", "हिन्दी", "Italiano", "Nederlands", "한국어", "Русский", "Türkçe", 
            "Polski", "Svenska", "Norsk", "Dansk", "Ελληνικά", "עברית"
        ];
    }

    init() {
        this.container = document.getElementById(this.containerId);
        if (!this.container) return;

        this.injectStyles();
        this.renderSkeleton();

        onAuthStateChanged(auth, async (user) => {
            this.currentUser = user;
            if (user) {
                try {
                    await this.fetchData();
                } catch (e) {
                    console.error("Error loading profile data:", e);
                }
            }
            this.render();
            this.attachEvents();
        });
    }

    async fetchData() {
        if (!this.currentUser) return;
        
        // 1. Fetch User Preferences
        const userDocRef = doc(db, "users", this.currentUser.uid);
        const userSnap = await getDoc(userDocRef);
        if (userSnap.exists()) {
            this.userData = userSnap.data();
        }

        // 1b. Fetch Real Orders
        this.isOrdersLoading = true;
        const ordersRef = collection(db, "orders");
        const q = query(ordersRef, where("userId", "==", this.currentUser.uid), orderBy("createdAt", "desc"), limit(20));
        const orderSnap = await getDocs(q);
        this.orders = orderSnap.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
        this.isOrdersLoading = false;

        // 1c. Fetch Weekly Fitness Stats
        const startOfWeek = new Date();
        startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
        startOfWeek.setHours(0, 0, 0, 0);
        const weekQuery = query(
            collection(db, 'users', this.currentUser.uid, 'fitness_history'),
            where('timestamp', '>=', startOfWeek.getTime())
        );
        const weekSnap = await getDocs(weekQuery);
        this.weekWorkoutCount = weekSnap.size;

        // 1c. Fetch Investments from localStorage
        const investedIds = JSON.parse(localStorage.getItem('fg_invested_projects') || '[]');
        this.investments = CROWDFUNDING_PROJECTS.filter(p => investedIds.includes(p.id));

        // 1d. Fetch Saved Projects from localStorage
        const savedIds = getSavedProjects();
        this.savedProjects = CROWDFUNDING_PROJECTS.filter(p => savedIds.includes(p.id));

        // 2. Fetch Saved Items (Mocking the read from subcollection as requested)
        try {
            // In reality: await getDocs(collection(db, "users", this.currentUser.uid, "saved", "podcasts"));
            // For now, empty array
            this.savedItems = []; 
        } catch (e) {
            console.warn("Failed to fetch saved items", e);
        }

        // Apply Dark Mode if saved
        if (this.userData.darkMode) {
            document.body.setAttribute('data-theme', 'dark');
        }
    }

    calculateAge(dobString) {
        if (!dobString) return null;
        const today = new Date();
        const birthDate = new Date(dobString);
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    }

    injectStyles() {
        const styleId = 'fg-profile-styles';
        if (document.getElementById(styleId)) return;
        const css = `
            .fg-profile-container { max-width: 680px; margin: 40px auto; padding: 0 20px; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; }
            .fg-profile-card { background: #fff; border-radius: 12px; box-shadow: 0 2px 12px rgba(0,0,0,0.08); padding: 30px; margin-bottom: 24px; position: relative; }
            .fg-profile-divider { height: 1px; background: #f0f0f0; margin: 24px 0; }
            .fg-profile-section-header { font-size: 0.75rem; font-weight: 700; text-transform: uppercase; color: #888; letter-spacing: 0.5px; margin-bottom: 16px; }
            
            /* Header Section */
            .fg-profile-header { display: flex; flex-direction: column; align-items: center; text-align: center; }
            .fg-avatar-wrapper { position: relative; width: 100px; height: 100px; margin-bottom: 16px; }
            .fg-avatar-img { width: 100%; height: 100%; border-radius: 50%; object-fit: cover; border: 4px solid #f8f9fa; }
            .fg-camera-btn { position: absolute; bottom: 0; right: 0; width: 32px; height: 32px; background: #fff; border: 1px solid #e0e0e0; border-radius: 50%; display: flex; align-items: center; justify-content: center; cursor: pointer; box-shadow: 0 2px 4px rgba(0,0,0,0.1); transition: transform 0.2s; }
            .fg-camera-btn:hover { transform: scale(1.05); background: #f8f9fa; }
            .fg-name-input { font-size: 1.5rem; font-weight: 700; text-align: center; border: 1px solid transparent; background: transparent; padding: 4px 8px; border-radius: 6px; width: 100%; max-width: 300px; outline: none; transition: border-color 0.2s, background 0.2s; }
            .fg-name-input:hover, .fg-name-input:focus { border-color: #e0e0e0; background: #f8f9fa; }
            .fg-email-text { color: #666; font-size: 0.95rem; margin-top: 4px; }

            /* Saved Section */
            .fg-saved-header { display: flex; justify-content: space-between; align-items: center; cursor: pointer; user-select: none; }
            .fg-saved-chevron { transition: transform 0.3s ease; width: 20px; height: 20px; opacity: 0.5; }
            .fg-saved-chevron.expanded { transform: rotate(180deg); }
            .fg-saved-content { display: none; margin-top: 20px; }
            .fg-saved-content.expanded { display: block; animation: fadeIn 0.3s ease; }
            .fg-tabs { display: flex; gap: 8px; margin-bottom: 20px; border-bottom: 1px solid #eee; padding-bottom: 12px; overflow-x: auto; }
            .fg-tab-btn { background: none; border: none; padding: 6px 12px; font-weight: 600; color: #666; cursor: pointer; border-radius: 6px; transition: all 0.2s; white-space: nowrap; }
            .fg-tab-btn:hover { background: #f5f5f5; color: #333; }
            .fg-tab-btn.active { background: #eef2ff; color: #1877f2; }
            .fg-empty-state { text-align: center; padding: 30px; color: #888; font-style: italic; background: #f9f9f9; border-radius: 8px; }

            /* Preferences */
            .fg-pref-row { display: flex; justify-content: space-between; align-items: center; padding: 12px 0; border-bottom: 1px solid #f5f5f5; }
            .fg-pref-row:last-child { border-bottom: none; }
            .fg-pref-label { font-size: 0.95rem; font-weight: 500; color: #333; }
            [data-theme="dark"] .fg-pref-row { border-bottom-color: var(--border-color); }
            [data-theme="dark"] .fg-pref-label { color: var(--text-color); }
            
            /* iOS Toggle */
            .fg-ios-toggle { position: relative; display: inline-block; width: 44px; height: 24px; }
            .fg-ios-toggle input { opacity: 0; width: 0; height: 0; }
            .fg-slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background-color: #ccc; transition: .3s; border-radius: 24px; }
            .fg-slider:before { position: absolute; content: ""; height: 20px; width: 20px; left: 2px; bottom: 2px; background-color: white; transition: .3s; border-radius: 50%; box-shadow: 0 1px 3px rgba(0,0,0,0.3); }
            input:checked + .fg-slider { background-color: #1877f2; }
            input:checked + .fg-slider:before { transform: translateX(20px); }

            /* Language Dropdown */
            .fg-lang-selector { position: relative; }
            .fg-lang-btn { background: #f5f5f5; border: 1px solid #e0e0e0; padding: 6px 12px; border-radius: 6px; cursor: pointer; font-size: 0.9rem; min-width: 100px; text-align: left; display: flex; justify-content: space-between; align-items: center; }
            .fg-lang-dropdown { position: absolute; top: 100%; right: 0; background: #fff; border: 1px solid #e0e0e0; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); width: 200px; max-height: 250px; overflow-y: auto; z-index: 100; display: none; margin-top: 4px; }
            .fg-lang-dropdown.open { display: block; }
            .fg-lang-search { padding: 8px; position: sticky; top: 0; background: #fff; border-bottom: 1px solid #f0f0f0; }
            .fg-lang-search input { width: 100%; padding: 6px; border: 1px solid #ddd; border-radius: 4px; outline: none; }
            .fg-lang-item { padding: 8px 12px; cursor: pointer; transition: background 0.2s; font-size: 0.9rem; }
            .fg-lang-item:hover { background: #f0f0f0; }
            .fg-lang-item.selected { background: #eef2ff; color: #1877f2; font-weight: 600; }

            /* Main Tabs */
            .fg-main-tabs { display: flex; gap: 20px; margin-bottom: 24px; border-bottom: 2px solid var(--border-color); padding: 0 10px; }
            .fg-main-tab-btn { background: none; border: none; padding: 12px 4px; font-weight: 700; color: var(--text-secondary); cursor: pointer; position: relative; font-size: 0.95rem; }
            .fg-main-tab-btn.active { color: var(--primary-blue); }
            .fg-main-tab-btn.active::after { content: ''; position: absolute; bottom: -2px; left: 0; right: 0; height: 2px; background: var(--primary-blue); }
            [data-theme="dark"] .fg-main-tab-btn.active { color: #4599ff; }

            /* Order History Enhancements */
            .fg-order-history-item__link { font-size: 0.85rem; color: var(--primary-blue); text-decoration: underline; background: none; border: none; cursor: pointer; padding: 0; margin-top: 8px; display: block; }
            .fg-order-status-badge { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 0.7rem; font-weight: 700; text-transform: uppercase; }
            .fg-order-status-badge--paid { background: #e8f5e9; color: #2e7d32; }

            /* Account Section */
            .fg-account-btn { display: block; width: 100%; text-align: left; padding: 12px 0; background: none; border: none; font-size: 0.95rem; cursor: pointer; border-bottom: 1px solid #f5f5f5; transition: color 0.2s; }
            .fg-account-btn:last-child { border-bottom: none; }
            .fg-account-btn.disabled { color: #ccc; cursor: not-allowed; }
            .fg-account-btn:not(.disabled) { color: var(--text-color); }
            .fg-account-btn.danger { color: #dc3545; }
            .fg-account-btn.danger:hover { color: #bd2130; }
            .fg-signout-btn { width: 100%; margin-top: 10px; text-align: center; justify-content: center; }
            
            /* Forms */
            .fg-inline-form { margin-top: 10px; padding: 15px; background: var(--bg-color); border-radius: 8px; border: 1px solid var(--border-color); }
            .fg-form-input { width: 100%; padding: 8px; margin-bottom: 10px; border-radius: 6px; border: 1px solid var(--border-color); background: var(--input-bg); color: var(--text-color); box-sizing: border-box; }
            .fg-form-actions { display: flex; gap: 10px; }
            .fg-msg-success { color: #28a745; font-size: 0.9rem; margin-top: 8px; }
            .fg-msg-error { color: #dc3545; font-size: 0.9rem; margin-top: 8px; }
            
            /* Fitness Dashboard Widget */
            .fg-fitness-widget { background: linear-gradient(135deg, #007AFF 0%, #FF2D55 100%); border-radius: 16px; padding: 20px; color: #fff; margin-bottom: 24px; display: flex; align-items: center; justify-content: space-between; }
            .fg-fit-widget-info h4 { font-size: 0.75rem; font-weight: 800; text-transform: uppercase; opacity: 0.9; margin-bottom: 4px; letter-spacing: 1px; }
            .fg-fit-widget-info .count { font-size: 2rem; font-weight: 900; line-height: 1; }
            .fg-fit-widget-icon { font-size: 2.5rem; opacity: 0.3; }
            .fg-fit-widget-link { background: rgba(255,255,255,0.2); border: none; color: #fff; padding: 8px 16px; border-radius: 99px; font-weight: 700; font-size: 0.8rem; cursor: pointer; backdrop-filter: blur(4px); }
            .fg-fit-widget-link:hover { background: rgba(255,255,255,0.3); }

            /* Skeleton */
            .skeleton-bar { height: 16px; background: #f0f0f0; border-radius: 4px; margin-bottom: 8px; animation: pulse 1.5s infinite; }
            .skeleton-circle { width: 100px; height: 100px; border-radius: 50%; background: #f0f0f0; margin: 0 auto 16px; animation: pulse 1.5s infinite; }
            .skeleton-order-row { display: flex; justify-content: space-between; padding: 15px; border: 1px solid #f0f0f0; border-radius: 8px; margin-bottom: 12px; }
            @keyframes pulse { 0% { opacity: 0.6; } 50% { opacity: 1; } 100% { opacity: 0.6; } }
            @keyframes fadeIn { from { opacity: 0; transform: translateY(-5px); } to { opacity: 1; transform: translateY(0); } }
        `;
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = css;
        document.head.appendChild(style);
    }

    renderSkeleton() {
        this.container.innerHTML = `
            <div class="fg-profile-container">
                <div class="fg-profile-card">
                    <div class="skeleton-circle"></div>
                    <div class="skeleton-bar" style="width: 200px; margin: 0 auto 8px;"></div>
                    <div class="skeleton-bar" style="width: 150px; margin: 0 auto;"></div>
                </div>
                <div class="fg-profile-card">
                    <div class="skeleton-bar" style="width: 100px;"></div>
                    <div class="skeleton-bar" style="width: 100%;"></div>
                    <div class="skeleton-bar" style="width: 100%;"></div>
                </div>
            </div>
        `;
    }

    renderOrderSkeleton() {
        return Array(3).fill(0).map(() => `
            <div class="skeleton-order-row">
                <div>
                    <div class="skeleton-bar" style="width: 120px;"></div>
                    <div class="skeleton-bar" style="width: 80px;"></div>
                </div>
                <div class="skeleton-bar" style="width: 60px;"></div>
            </div>
        `).join('');
    }

    render() {
        const user = this.currentUser;

        if (!user) {
            this.container.innerHTML = `
                <div class="fg-profile-container">
                    <section class="fg-profile-card" style="text-align: center;">
                        <img src="/images/logo2.png" style="width: 80px; margin-bottom: 20px;">
                        <h2 class="fg-auth-modal__title">Sign in to view your orders</h2>
                        <p class="fg-auth-modal__text">Keep track of your purchases and manage your preferences.</p>
                        <button class="fg-btn fg-btn--primary" id="btn-login-redirect">Sign In / Sign Up</button>
                    </section>
                </div>
            `;
            return;
        }

        // Defaults
        const photoUrl = user.photoURL || '/images/logo2.png';
        const name = this.userData.displayName || user.displayName || 'FootballGirl Fan';
        const email = user.email || 'No email';
        const username = this.userData.username || 'No username set';
        const preferredLang = this.userData.preferredLanguage || 'English';
        const age = this.calculateAge(this.userData.dob);
        const notifications = this.userData.notifications !== false; // Default true
        const darkMode = this.userData.darkMode === true; // Default false

        // Mobile check for camera input
        const isMobile = window.innerWidth <= 768 || (navigator.maxTouchPoints > 0);
        const captureAttr = isMobile ? 'capture="environment"' : '';

        const settingsContent = `
            <!-- SECTION: USERNAME -->
            <section class="fg-profile-card">
                <div class="fg-profile-section-header">USERNAME</div>
                <div style="margin-bottom: 15px;">
                    <p style="font-size: 0.9rem; color: #666; margin-bottom: 5px;">Current: <strong>${username}</strong></p>
                    <p style="font-size: 0.8rem; color: #888; margin-bottom: 10px;">You can only change your username once every 30 days.</p>
                    <div style="display: flex; gap: 10px;">
                        <input type="text" id="username-input" class="fg-form-input" placeholder="New username" style="margin-bottom: 0;">
                        <button class="fg-btn" id="save-username-btn">Save</button>
                    </div>
                    <p id="username-msg" style="display:none; font-size: 0.85rem; margin-top: 8px;"></p>
                </div>
            </section>

            <!-- SECTION: PREFERENCES -->
            <section class="fg-profile-card">
                <div class="fg-profile-section-header">PREFERENCES</div>
                <div class="fg-pref-row">
                    <div class="fg-pref-label">Language</div>
                    <div class="fg-lang-selector" id="lang-wrapper">
                        <button class="fg-lang-btn" id="lang-btn">
                            ${preferredLang} 
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
                        </button>
                        <div class="fg-lang-dropdown" id="lang-dropdown">
                            <div class="fg-lang-search"><input type="text" id="lang-search" placeholder="Search..." autocomplete="off"></div>
                            <div id="lang-list">${this.renderLanguageList(preferredLang)}</div>
                        </div>
                    </div>
                </div>
                <div class="fg-pref-row">
                    <div class="fg-pref-label">Push Notifications</div>
                    <label class="fg-ios-toggle">
                        <input type="checkbox" id="toggle-notif" ${notifications ? 'checked' : ''}>
                        <span class="fg-slider"></span>
                    </label>
                </div>
                <div class="fg-pref-row">
                    <div class="fg-pref-label">Dark Mode</div>
                    <label class="fg-ios-toggle">
                        <input type="checkbox" id="toggle-dark" ${darkMode ? 'checked' : ''}>
                        <span class="fg-slider"></span>
                    </label>
                </div>
            </section>

            <!-- SECTION: ACCOUNT -->
            <section class="fg-profile-card">
                <div class="fg-profile-section-header">ACCOUNT</div>
                <div id="pwd-section">
                    <button class="fg-account-btn" id="btn-pwd-toggle">Change Password</button>
                    <div class="fg-inline-form" id="pwd-form" style="display: none;">
                        <p id="pwd-msg" class="fg-msg-error" style="display:none;"></p>
                        <input type="password" id="pwd-current" class="fg-form-input" placeholder="Current Password">
                        <input type="password" id="pwd-new" class="fg-form-input" placeholder="New Password (min 8 chars)">
                        <input type="password" id="pwd-confirm" class="fg-form-input" placeholder="Confirm New Password">
                        <div class="fg-form-actions">
                            <button class="fg-btn fg-btn--primary" id="btn-pwd-save">Update Password</button>
                            <button class="fg-btn" id="btn-pwd-cancel">Cancel</button>
                        </div>
                    </div>
                </div>
                <div id="del-section">
                    <button class="fg-account-btn danger" id="btn-del-toggle">Delete Account</button>
                    <div class="fg-inline-form" id="del-form" style="display: none; border-color: #dc3545; background: rgba(220, 53, 69, 0.05);">
                        <p style="color: #dc3545; font-weight: 600; margin-bottom: 10px;">This will permanently delete your account and all data. This cannot be undone.</p>
                        <input type="text" id="del-input" class="fg-form-input" placeholder="Type DELETE to confirm">
                        <div class="fg-form-actions">
                            <button class="fg-btn fg-btn--danger" id="btn-del-confirm" disabled>Delete Permanently</button>
                            <button class="fg-btn" id="btn-del-cancel">Cancel</button>
                        </div>
                    </div>
                </div>
                <div style="margin-top: 16px;">
                    <button class="fg-btn fg-btn--primary fg-signout-btn" id="btn-signout">Sign Out</button>
                </div>
            </section>
        `;

        const ordersContent = `
            <section class="fg-profile-card">
                <div class="fg-profile-section-header">MY ORDERS</div>
                ${this.viewingOrderId ? this.renderOrderDetail() : this.renderOrderList()}
            </section>
        `;

        const investmentsContent = `
            <section class="fg-profile-card">
                <div class="fg-profile-section-header">MY INVESTMENTS</div>
                ${this.renderInvestmentList()}
            </section>
        `;

        const savedContent = `
            <section class="fg-profile-card">
                <div class="fg-profile-section-header" style="margin-bottom: 20px;">SAVED ITEMS</div>
                <div class="fg-tabs">
                    <button class="fg-tab-btn ${this.activeSavedTab === 'podcasts' ? 'active' : ''}" data-tab="podcasts">Podcasts</button>
                    <button class="fg-tab-btn" data-tab="projects">Projects</button>
                    <button class="fg-tab-btn" data-tab="articles">Articles</button>
                    <button class="fg-tab-btn" data-tab="creator-onboarding">Creator Onboarding</button>
                    <button class="fg-tab-btn" data-tab="creator-dashboard">Creator Dashboard</button>

                    <button class="fg-tab-btn" data-tab="books">Books</button>
                    <button class="fg-tab-btn" data-tab="videos">Videos</button>
                </div>
                ${this.activeSavedTab === 'projects' && this.savedProjects.length > 0 ? `
                    <button class="fg-btn" style="width: auto; margin-bottom: 15px; font-size: 0.8rem;" data-clear-all-saved>Clear All Saved Projects</button>
                ` : ''}
                <div id="tab-content">${this.renderSavedContent()}</div>
            </section>
        `;

        this.container.innerHTML = `
            <div class="fg-profile-container">
                <!-- FITNESS DASHBOARD WIDGET -->
                <section class="fg-fitness-widget">
                    <div class="fg-fit-widget-info">
                        <h4>Fitness Dashboard</h4>
                        <div class="count">${this.weekWorkoutCount} <span style="font-size: 1rem; font-weight: 600;">Workouts This Week</span></div>
                    </div>
                    <div style="display: flex; flex-direction: column; align-items: flex-end; gap: 8px;">
                        <div class="fg-fit-widget-icon">⚡</div>
                        <button class="fg-fit-widget-link" onclick="window.app.handleNavigation('fitness')">Train Now</button>
                    </div>
                </section>

                <section class="fg-profile-card">
                    <div class="fg-profile-header">
                        <div class="fg-avatar-wrapper">
                            <img src="${photoUrl}" alt="Profile" class="fg-avatar-img" id="profile-img">
                            <label class="fg-camera-btn" title="Change Photo">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#666" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle></svg>
                                <input type="file" id="photo-upload" accept="image/*" ${captureAttr} style="display: none;">
                            </label>
                        </div>
                        <input type="text" id="name-input" class="fg-name-input" value="${name}" spellcheck="false" aria-label="Edit Name">
                        <div class="fg-email-text">${email}${age ? ` • ${age} years old` : ''}</div>
                    </div>
                </section>

                <div class="fg-main-tabs">
                    <button class="fg-main-tab-btn ${this.activeTab === 'settings' ? 'active' : ''}" data-main-tab="settings">Settings</button>
                    <button class="fg-main-tab-btn ${this.activeTab === 'orders' ? 'active' : ''}" data-main-tab="orders">My Orders</button>
                    <button class="fg-main-tab-btn ${this.activeTab === 'investments' ? 'active' : ''}" data-main-tab="investments">My Investment</button>
                    <button class="fg-main-tab-btn ${this.activeTab === 'saved' ? 'active' : ''}" data-main-tab="saved">Saved</button>
                </div>

                <div id="tab-container">
                    ${this.activeTab === 'settings' ? settingsContent : ''}
                    ${this.activeTab === 'orders' ? ordersContent : ''}
                    ${this.activeTab === 'investments' ? investmentsContent : ''}
                    ${this.activeTab === 'saved' ? savedContent : ''}
                </div>
            </div>
        `;
    }

    renderOrderList() {
        return `
            <div class="fg-order-history-list">
                ${this.isOrdersLoading ? this.renderOrderSkeleton() : 
                  this.orders.length > 0 ? this.orders.map(order => `
                        <div class="fg-order-history-item">
                            <div class="fg-order-history-item__info">
                                <h4>Order #${order.id.substring(0, 8)}</h4>
                                <p>${order.createdAt?.toDate().toLocaleDateString() || 'Pending'}</p>
                                <p style="font-size: 0.8rem;">Items: ${order.items?.length || 0}</p>
                                <button class="fg-order-history-item__link" data-view-order="${order.id}">View Order Details</button>
                            </div>
                            <div class="fg-order-history-item__meta">
                                <span class="fg-order-history-item__total">${order.total}</span>
                                <span class="fg-order-status-badge ${order.status === 'shipped' ? 'fg-order-status-badge--paid' : ''}">${order.status || 'Paid'}</span>
                            </div>
                        </div>
                    `).join('') : `
                        <div class="fg-empty-state">
                            <p>No orders yet</p>
                            <a href="#" id="start-shopping-link" style="color: var(--primary-blue); font-weight: 700; text-decoration: underline;">Start Shopping</a>
                        </div>
                    `}
            </div>
        `;
    }

    renderOrderDetail() {
        const order = this.orders.find(o => o.id === this.viewingOrderId);
        if (!order) return '<p>Order not found</p>';

        const { items, total, shippingAddress, status, trackingNumber, carrier, shippingMethod, shippingCost, estimatedDeliveryRange } = order;
        const isShipped = status === 'shipped';

        return `
            <div class="fg-order-conf" style="box-shadow: none; border: none; padding: 0;">
                <button class="fg-order-history-item__link" id="back-to-order-list" style="margin-bottom: 20px;">← Back to List</button>
                <div class="fg-order-conf__body">
                    <section class="fg-order-conf__section" style="padding: 0;">
                        <h3 style="margin-top: 0;">Delivery Summary</h3>
                        <p style="font-size: 0.9rem; color: var(--text-secondary); margin-bottom: 1rem;">
                            Method: <strong>${shippingMethod || 'Standard Delivery'}</strong> (${shippingCost || '£3.99'})
                        </p>
                        ${isShipped ? `
                            <div class="fg-order-conf__tracking" style="margin-bottom: 1.5rem; padding: 1.25rem; background: var(--bg-secondary); border-radius: 12px; border: 1px solid var(--border-color);">
                                <p style="margin-bottom: 0.5rem;"><strong>Status:</strong> Shipped</p>
                                <p style="margin-bottom: 0.5rem;"><strong>Carrier:</strong> ${carrier || 'N/A'}</p>
                                <p style="margin: 0;"><strong>Tracking:</strong> ${trackingNumber || 'N/A'}</p>
                            </div>
                        ` : `
                            <p class="fg-order-conf__delivery-time">Estimated delivery: ${estimatedDeliveryRange || '3-5 working days'}</p>
                        `}
                        <address class="fg-order-conf__address">
                            ${shippingAddress.line1}<br>
                            ${shippingAddress.city}, ${shippingAddress.postalCode}<br>
                            ${shippingAddress.country}
                        </address>
                    </section>
                    <section class="fg-order-conf__section" style="padding: 0; margin-top: 24px;">
                        <h3>Item Summary</h3>
                        <div class="fg-order-conf__items">
                            ${items.map(item => `
                                <div class="fg-order-conf__item">
                                    <div class="fg-order-conf__item-info">
                                        <span class="fg-order-conf__item-name">${item.name}</span>
                                        <span class="fg-order-conf__item-meta">Size: ${item.size} | Qty: ${item.quantity}</span>
                                    </div>
                                    <span class="fg-order-conf__item-price">${item.price}</span>
                                </div>
                            `).join('')}
                        </div>
                        <div class="fg-order-conf__total">
                            <span>Total</span>
                            <span>${total}</span>
                        </div>
                    </section>
                </div>
                <div class="fg-order-conf__actions" style="margin-top: 24px; padding: 0;">
                    <button class="fg-btn" id="print-order-btn">Download Invoice (PDF)</button>
                </div>
            </div>
        `;
    }

    renderInvestmentList() {
        if (this.investments.length === 0) {
            return `<div class="fg-empty-state"><p>You haven't invested in any projects yet.</p>
                    <a href="#" id="browse-projects-link" style="color: var(--primary-blue); font-weight: 700; text-decoration: underline;">Browse Projects</a></div>`;
        }
        return `
            <div class="fg-order-history-list">
                ${this.investments.map(p => `
                    <div class="fg-order-history-item">
                        <div class="fg-order-history-item__info">
                            <h4>${p.title}</h4>
                            <p>${p.category}</p>
                            <button class="fg-order-history-item__link" data-view-invest-project="${p.id}">View Project Details</button>
                        </div>
                        <div class="fg-order-history-item__meta">
                            <span class="fg-order-status-badge fg-order-status-badge--paid">Active</span>
                        </div>
                    </div>
                `).join('')}
            </div>
        `;
    }

    renderSavedContent() {
        if (this.activeSavedTab === 'projects') {
            if (this.savedProjects.length === 0) return '<div class="fg-empty-state">No saved projects</div>';
            return `
                <div class="fg-order-history-list">
                    ${this.savedProjects.map(p => `
                        <div class="fg-order-history-item">
                            <div class="fg-order-history-item__info">
                                <h4>${p.title}</h4>
                                <p>${p.category || 'Crowdfunding'}</p>
                                <div style="display: flex; gap: 12px;">
                                    <button class="fg-order-history-item__link" data-view-invest-project="${p.id}">View Details</button>
                                    <button class="fg-order-history-item__link" style="color: #dc3545;" data-remove-saved-project="${p.id}">Remove</button>
                                </div>
                            </div>
                        </div>
                    `).join('')}
                </div>
            `;
        }
        if (this.activeSavedTab !== 'podcasts') {
            return `<div class="fg-empty-state">Coming soon</div>`;
        }
        if (this.savedItems.length === 0) {
            return `<div class="fg-empty-state">No saved podcasts yet</div>`;
        }
        // If we had items, we would map them here. Mock data is empty for now.
        return '';
    }

    renderLanguageList(selected, filter = '') {
        const filtered = this.languages.filter(l => l.toLowerCase().includes(filter.toLowerCase()));
        if (filtered.length === 0) return `<div class="fg-lang-item" style="color:#999; cursor:default;">No results</div>`;
        
        return filtered.map(lang => `
            <div class="fg-lang-item ${lang === selected ? 'selected' : ''}" data-lang="${lang}">
                ${lang}
            </div>
        `).join('');
    }

    attachEvents() {
        // 1. Photo Upload
        const fileInput = this.container.querySelector('#photo-upload');
        fileInput?.addEventListener('change', (e) => {
            if (e.target.files[0]) {
                console.log('Selected file:', e.target.files[0]);
                // Visual feedback only
                const img = this.container.querySelector('#profile-img');
                img.src = URL.createObjectURL(e.target.files[0]);
            }
        });

        // 2. Name Edit
        const nameInput = this.container.querySelector('#name-input');
        const saveName = async () => {
            const newName = nameInput.value.trim();
            if (newName && newName !== this.currentUser.displayName) {
                try {
                    await updateProfile(this.currentUser, { displayName: newName });
                    await this.savePreference({ displayName: newName });
                    console.log('Name updated to:', newName);
                } catch (e) {
                    console.error('Error updating name:', e);
                    nameInput.value = this.currentUser.displayName; // Revert
                }
            }
        };
        nameInput?.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                nameInput.blur();
            }
        });
        nameInput?.addEventListener('blur', saveName);

        // Email Verification Resend
        const resendBtn = this.container.querySelector('#resend-verify-btn');
        resendBtn?.addEventListener('click', async () => {
            try {
                await sendVerification(this.currentUser);
                resendBtn.textContent = "Verification email sent";
                resendBtn.disabled = true;
            } catch (error) {
                console.error(error);
                alert("Error sending email: " + error.message);
            }
        });

        // 2b. Username Save
        const usernameBtn = this.container.querySelector('#save-username-btn');
        const usernameInput = this.container.querySelector('#username-input');
        const usernameMsg = this.container.querySelector('#username-msg');

        usernameBtn?.addEventListener('click', async () => {
            const newUsername = usernameInput.value.trim();
            const usernameRegex = /^[a-zA-Z0-9_]{3,20}$/;

            if (!usernameRegex.test(newUsername)) {
                usernameMsg.textContent = "Username must be 3-20 chars, alphanumeric or underscores only.";
                usernameMsg.className = "fg-msg-error";
                usernameMsg.style.display = "block";
                return;
            }

            // Check 30-day limit
            if (this.userData.usernameLastChanged) {
                const lastChanged = this.userData.usernameLastChanged.toDate();
                const now = new Date();
                const diffTime = Math.abs(now - lastChanged);
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
                if (diffDays < 30) {
                    usernameMsg.textContent = `You can only change your username once every 30 days. Try again in ${30 - diffDays} days.`;
                    usernameMsg.className = "fg-msg-error";
                    usernameMsg.style.display = "block";
                    return;
                }
            }

            // Transaction for username reservation
            try {
                await runTransaction(db, async (transaction) => {
                    const usernameRef = doc(db, "usernames", newUsername);
                    const usernameSnap = await transaction.get(usernameRef);

                    if (usernameSnap.exists()) {
                        throw new Error("Username already taken. Please choose another.");
                    }

                    if (this.userData.username) {
                        transaction.delete(doc(db, "usernames", this.userData.username));
                    }

                    transaction.set(usernameRef, { uid: this.currentUser.uid });
                });

                // Save to user profile
                await this.savePreference({ username: newUsername, usernameLastChanged: serverTimestamp() });
                
                usernameMsg.textContent = "Username saved";
                usernameMsg.className = "fg-msg-success";
                usernameMsg.style.display = "block";
                setTimeout(() => { usernameMsg.style.display = 'none'; this.fetchData().then(() => this.render()); }, 2000);

            } catch (e) {
                console.error("Error saving username:", e);
                usernameMsg.className = "fg-msg-error";
                usernameMsg.style.display = "block";
                
                if (e.message === "Username already taken. Please choose another.") {
                    usernameMsg.textContent = e.message;
                } else {
                    usernameMsg.textContent = "Error saving username. Please try again.";
                }
            }
        });

        // 3. Tab Switching
        this.container.querySelectorAll('.fg-main-tab-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                this.activeTab = e.target.dataset.mainTab;
                this.viewingOrderId = null;
                this.render();
                this.attachEvents();
            });
        });

        // 4. Order Interactions
        this.container.querySelectorAll('[data-view-order]').forEach(btn => {
            btn.addEventListener('click', (e) => {
                this.viewingOrderId = e.target.dataset.viewOrder;
                this.render();
                this.attachEvents();
            });
        });

        // Handle Saved Project actions (View/Remove) via delegation to handle dynamic sub-tab rendering
        this.container.addEventListener('click', (e) => {
            const viewBtn = e.target.closest('[data-view-invest-project]');
            const removeBtn = e.target.closest('[data-remove-saved-project]');

            if (viewBtn) {
                window.dispatchEvent(new CustomEvent('fg:navigate', { 
                    detail: { page: 'crowdfunding-detail', projectId: viewBtn.dataset.viewInvestProject } 
                }));
            }

            if (removeBtn) {
                const projectId = removeBtn.dataset.removeSavedProject;
                unsaveProject(projectId);
                this.savedProjects = this.savedProjects.filter(p => p.id !== projectId);
                const tabContent = this.container.querySelector('#tab-content');
                if (tabContent) tabContent.innerHTML = this.renderSavedContent();
            }
        });

        this.container.querySelector('#browse-projects-link')?.addEventListener('click', (e) => {
            e.preventDefault();
            window.dispatchEvent(new CustomEvent('fg:navigate', { detail: { page: 'fund' } }));
        });

        this.container.querySelector('#back-to-order-list')?.addEventListener('click', () => {
            this.viewingOrderId = null;
            this.render();
            this.attachEvents();
        });

        this.container.querySelector('#print-order-btn')?.addEventListener('click', () => {
            window.print();
        });

        this.container.querySelectorAll('.fg-tab-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                this.activeSavedTab = e.target.dataset.tab;
                // Re-render saved content area
                const tabContent = this.container.querySelector('#tab-content');
                tabContent.innerHTML = this.renderSavedContent();
                // Update classes
                this.container.querySelectorAll('.fg-tab-btn').forEach(b => b.classList.remove('active'));
                e.target.classList.add('active');
            });
        });

        // 4. Language Selector
        const langBtn = this.container.querySelector('#lang-btn');
        const langDropdown = this.container.querySelector('#lang-dropdown');
        const langSearch = this.container.querySelector('#lang-search');
        const langList = this.container.querySelector('#lang-list');

        // Toggle Dropdown
        const toggleLang = (e) => {
            e.stopPropagation();
            const isOpen = langDropdown.classList.contains('open');
            if (isOpen) {
                langDropdown.classList.remove('open');
            } else {
                langDropdown.classList.add('open');
                langSearch.focus();
            }
        };
        langBtn?.addEventListener('click', toggleLang);

        // Search Filter
        langSearch?.addEventListener('input', (e) => {
            langList.innerHTML = this.renderLanguageList(this.userData.preferredLanguage, e.target.value);
        });

        // Select Language (Delegation)
        langList?.addEventListener('click', async (e) => {
            const item = e.target.closest('.fg-lang-item');
            if (item) {
                const lang = item.dataset.lang;
                console.log('Language selected:', lang);
                langDropdown.classList.remove('open');
                this.userData.preferredLanguage = lang; // Optimistic update
                langBtn.innerHTML = `${lang} <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"></polyline></svg>`;
                await this.savePreference({ preferredLanguage: lang });
                // Reset search
                langSearch.value = '';
                langList.innerHTML = this.renderLanguageList(lang);
            }
        });

        // Close dropdown when clicking outside
        document.addEventListener('click', (e) => {
            if (langDropdown && langDropdown.classList.contains('open') && !e.target.closest('#lang-wrapper')) {
                langDropdown.classList.remove('open');
            }
        });

        // 5. Toggles
        this.container.querySelector('#toggle-notif')?.addEventListener('change', async (e) => {
            await this.savePreference({ notifications: e.target.checked });
        });

        this.container.querySelector('#toggle-dark')?.addEventListener('change', async (e) => {
            const isDark = e.target.checked;
            if (isDark) {
                document.body.setAttribute('data-theme', 'dark');
            } else {
                document.body.removeAttribute('data-theme');
            }
            await this.savePreference({ darkMode: isDark });
        });

        // 7. Change Password
        const pwdToggle = this.container.querySelector('#btn-pwd-toggle');
        const pwdForm = this.container.querySelector('#pwd-form');
        const pwdMsg = this.container.querySelector('#pwd-msg');

        pwdToggle?.addEventListener('click', () => {
            // Check if Google user
            const isGoogle = this.currentUser.providerData.some(p => p.providerId === 'google.com');
            if (isGoogle) {
                alert("Password change is not available for Google accounts. Please manage your security settings via Google.");
                return;
            }
            pwdForm.style.display = 'block';
            pwdToggle.style.display = 'none';
        });

        this.container.querySelector('#btn-pwd-cancel')?.addEventListener('click', () => {
            pwdForm.style.display = 'none';
            pwdToggle.style.display = 'block';
            pwdMsg.style.display = 'none';
            this.container.querySelectorAll('#pwd-form input').forEach(i => i.value = '');
        });

        this.container.querySelector('#btn-pwd-save')?.addEventListener('click', async () => {
            const current = this.container.querySelector('#pwd-current').value;
            const newPwd = this.container.querySelector('#pwd-new').value;
            const confirm = this.container.querySelector('#pwd-confirm').value;

            if (newPwd.length < 8) { pwdMsg.textContent = 'Password must be at least 8 chars.'; pwdMsg.style.display = 'block'; return; }
            if (newPwd !== confirm) { pwdMsg.textContent = 'Passwords do not match.'; pwdMsg.style.display = 'block'; return; }
            if (!current) { pwdMsg.textContent = 'Please enter current password.'; pwdMsg.style.display = 'block'; return; }

            try {
                const cred = EmailAuthProvider.credential(this.currentUser.email, current);
                await reauthenticateWithCredential(this.currentUser, cred);
                await updatePassword(this.currentUser, newPwd);
                
                pwdMsg.textContent = 'Password updated successfully!';
                pwdMsg.className = 'fg-msg-success';
                pwdMsg.style.display = 'block';
                setTimeout(() => this.container.querySelector('#btn-pwd-cancel').click(), 2000);
            } catch (e) {
                pwdMsg.textContent = e.message;
                pwdMsg.className = 'fg-msg-error';
                pwdMsg.style.display = 'block';
            }
        });

        // 8. Delete Account
        const delToggle = this.container.querySelector('#btn-del-toggle');
        const delForm = this.container.querySelector('#del-form');
        const delInput = this.container.querySelector('#del-input');
        const delConfirm = this.container.querySelector('#btn-del-confirm');

        delToggle?.addEventListener('click', () => {
            delForm.style.display = 'block';
            delToggle.style.display = 'none';
        });

        this.container.querySelector('#btn-del-cancel')?.addEventListener('click', () => {
            delForm.style.display = 'none';
            delToggle.style.display = 'block';
            delInput.value = '';
        });

        delInput?.addEventListener('input', (e) => {
            delConfirm.disabled = e.target.value !== 'DELETE';
        });

        delConfirm?.addEventListener('click', async () => {
            try {
                await deleteDoc(doc(db, "users", this.currentUser.uid));
                await deleteUser(this.currentUser);
                if (window.app) window.app.handleNavigation('home');
            } catch (e) {
                if (e.code === 'auth/requires-recent-login') {
                    alert("For security, please sign out and sign back in before deleting your account.");
                } else {
                    alert("Error deleting account: " + e.message);
                }
            }
        });

        // 6. Sign Out
        this.container.querySelector('#btn-signout')?.addEventListener('click', () => {
            logOut().then(() => {
                if (window.app) window.app.handleNavigation('home');
            });
        });
    }

    async savePreference(data) {
        if (!this.currentUser) return;
        try {
            const userRef = doc(db, "users", this.currentUser.uid);
            await setDoc(userRef, data, { merge: true });
        } catch (e) {
            console.error("Error saving preference:", e);
            // Optionally show error toast here
        }
    }
}